import type { SidebarSection } from '../components/Sidebar/Sidebar';
import type { StepChangeSummary } from '../types/alternativePlan';

/**
 * Canonical navigation labels — single source of truth for both platforms.
 * Mobile bottom nav and desktop sidebar must use these strings.
 */
export const NAV_LABELS = {
  HOME: 'Home',
  TOOLS: 'Tools',
  PLANS: 'Plans',
  PROJECTION: 'Projection',
  ACCOUNT: 'Account',
} as const;

/**
 * Canonical projection tab definitions.
 * shortLabel — used by mobile horizontal tab scroll (compact).
 * label      — used by desktop sidebar nav (full descriptive name).
 * Both must refer to the same underlying view.
 */
export const PROJECTION_TABS = [
  { id: 'overview',     shortLabel: 'Overview',        label: 'Overview' },
  { id: 'year-table',   shortLabel: 'Year Table',      label: 'Year-by-Year Table' },
  { id: 'scenarios',    shortLabel: 'Scenarios',        label: 'Scenario Analysis' },
  { id: 'shock',        shortLabel: 'Market Shock',    label: 'Market Shock' },
  { id: 'historical',   shortLabel: 'Historical',      label: 'Historical Scenarios' },
  { id: 'probability',  shortLabel: 'Probability',     label: 'Probability Analysis' },
  { id: 'sensitivity',  shortLabel: 'Sensitivity',     label: 'Spending Sensitivity' },
  { id: 'deferral',     shortLabel: 'Deferral',        label: 'Pension Deferral' },
  { id: 'earliest',     shortLabel: 'Retire When?',    label: 'When can I retire?' },
  { id: 'iht',          shortLabel: 'Inheritance Tax', label: 'Inheritance Tax' },
] as const;

/**
 * Wizard step definitions shared across sidebar callers.
 */
export const WIZARD_STEPS = [
  { number: 1, label: 'Plan Basics' },
  { number: 2, label: 'People' },
  { number: 3, label: 'Investments' },
  { number: 4, label: 'Property' },
  { number: 5, label: 'Income' },
  { number: 6, label: 'Spending' },
  { number: 7, label: 'Later Life & Estate' },
  { number: 8, label: 'Assumptions' },
  { number: 9, label: 'Strategy' },
  { number: 10, label: 'Review' },
] as const;

export function makePlanDetailsSidebarSection(
  subtitle?: string,
  stepChanges?: StepChangeSummary[] | null,
): SidebarSection {
  const hasAnyChanges = stepChanges?.some(sc => sc.hasChanges) ?? false;
  return {
    id: 'plan-details',
    label: 'Plan Details',
    subtitle,
    collapsible: true,
    defaultCollapsed: !hasAnyChanges,
    items: WIZARD_STEPS.map(step => ({
      id: `step-${step.number}`,
      label: step.label,
      number: step.number,
      status: 'completed' as const,
      badge: stepChanges?.find(sc => sc.stepNumber === step.number)?.hasChanges
        ? ('changed' as const)
        : undefined,
    })),
  };
}
